import {
  type ChangeEvent,
  forwardRef,
  useMemo,
  useState,
  type ForwardedRef,
} from "react";

import clsx from "clsx";

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { useFeatureFlags } from "@hooks/http";
import { useVirtualizer } from "@tanstack/react-virtual";

import { TextInput } from "../text-input";

import { Dropdown } from "./dropdown";
import { OptionItem } from "./option-item";
import { SelectButton } from "./select-button";
import { type BaseProps } from "./types";

interface Props<T> extends BaseProps<T> {
  value: T | null;
  onChange: (val: T | null) => void;
  hasSearch?: boolean;
}

const _Select = <T,>(
  {
    value,
    options,
    placeholder,
    disabled,
    clearable,
    size = "sm",
    hideCaret,
    className,
    buttonClassName,
    dropdownClassName,
    itemClassName,
    onChange,
    hasSearch,
  }: Props<T>,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  const { hasFeatureFlag } = useFeatureFlags();

  const [scrollRef, setScrollRef] = useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const filteredOptions = useMemo(() => {
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [options, searchQuery]);
  const selectedOption = useMemo(() => {
    return filteredOptions.find((e) => e.value === value) ?? null;
  }, [filteredOptions, value]);

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollRef,
    estimateSize: () => ({ sm: 32, md: 36, lg: 40 }[size]),
    overscan: 5,
  });

  return (
    <Listbox
      as="div"
      ref={ref}
      disabled={disabled}
      className={clsx("inline-block", className)}
      value={selectedOption?.value ?? null}
      onChange={onChange}
    >
      <ListboxButton
        as={SelectButton}
        className={buttonClassName}
        size={size}
        hideCaret={hideCaret}
        placeholder={placeholder}
        value={selectedOption?.label}
        onClear={clearable ? () => onChange(null) : undefined}
      />
      <ListboxOptions
        as={Dropdown}
        ref={setScrollRef}
        anchor={{ to: "bottom start", gap: 4 }}
        transition
        className={clsx(dropdownClassName, {
          "bg-white": !hasFeatureFlag("enableDashboardV2"),
        })}
      >
        {hasSearch && (
          <div className="sticky top-0 z-10 bg-white p-2 ">
            <TextInput
              placeholder="Search..."
              value={searchQuery}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(e.target.value)
              }
              wrapperClassName="w-full"
            />
          </div>
        )}
        <div
          className="relative"
          style={{
            height: `${virtualizer.getTotalSize()}px`,
          }}
        >
          {virtualizer.getVirtualItems().map((vItem) => {
            const option = filteredOptions[vItem.index];

            return (
              <ListboxOption
                as={OptionItem}
                key={String(option.value)}
                size={size}
                disabled={option.disabled}
                value={option.value}
                option={option}
                className={itemClassName}
                style={{
                  transform: `translateY(${vItem.start}px)`,
                }}
              />
            );
          })}
        </div>
      </ListboxOptions>
    </Listbox>
  );
};

// workaround to correctly infer generic type for forward reffed component
export const Select = forwardRef(_Select) as <T extends string | number>(
  props: Props<T> & { ref?: ForwardedRef<HTMLDivElement> },
) => ReturnType<typeof _Select>;
